import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LineChart, Trash, Receipt, Building2, Calendar, BarChart3 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UploadZone } from "@/components/UploadZone";
import { KpiCards } from "@/components/KpiCards";
import { TransactionsTable } from "@/components/TransactionsTable";
import { PnlView } from "@/components/PnlView";
import { CategoryManager } from "@/components/CategoryManager";
import { DuplicateBanner } from "@/components/DuplicateBanner";
import { TrendChart } from "@/components/TrendChart";
import { BankConnect } from "@/components/BankConnect";
import { api, periodLabel } from "@/lib/api";

const emptyPnl = { income: [], expense: [], total_income: 0, total_expense: 0, net_profit: 0, count: 0 };
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Dashboard() {
  const [period, setPeriod] = useState(() => localStorage.getItem("ledgerly_period") || thisMonth());
  const [company, setCompany] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [pnl, setPnl] = useState(emptyPnl);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [periods, setPeriods] = useState([]);
  const [dupGroups, setDupGroups] = useState([]);

  const load = useCallback(async () => {
    const [txns, p, dups, prds] = await Promise.all([
      api.transactions(period),
      api.pnl(period),
      api.duplicates(period),
      api.periods(),
    ]);
    setTransactions(txns);
    setPnl(p);
    setDupGroups(dups.groups || []);
    setPeriods(prds);
  }, [period]);

  useEffect(() => {
    api.categories().then(setCategories);
    api.company().then((c) => setCompany(c.name || ""));
  }, []);

  useEffect(() => {
    localStorage.setItem("ledgerly_period", period);
    load();
  }, [period, load]);

  const saveCompany = async (name) => {
    const res = await api.setCompany(name);
    setCompany(res.name);
  };

  const clearAll = async () => {
    await api.clearAll(period);
    toast.success(`Cleared ${periodLabel(period)}`);
    load();
  };

  const deleteTxn = async (id) => {
    await api.remove(id);
    toast.success("Transaction removed");
    load();
  };

  const generatedAt = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-black flex items-center justify-center">
              <Receipt className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-extrabold tracking-tight text-gray-900 leading-none">
                Ledgerly
              </h1>
              <p className="text-xs text-gray-500">Bank Statement → P&L</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Building2 className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="company-name-input"
                defaultValue={company}
                key={company}
                onBlur={(e) => e.target.value !== company && saveCompany(e.target.value)}
                placeholder="Company name"
                className="h-9 pl-8 w-44 bg-white border-gray-200"
              />
            </div>
            <div className="relative">
              <Calendar className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="month"
                data-testid="period-selector"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="h-9 pl-8 pr-2 rounded-md border border-gray-200 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-black focus:outline-none"
              />
            </div>
            <CategoryManager categories={categories} onChange={setCategories} />
            <BankConnect onChange={load} />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="clear-all-button"
                  className="bg-white border-gray-200 text-gray-900 hover:bg-gray-50"
                >
                  <Trash className="h-4 w-4 mr-1" /> Clear
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear {periodLabel(period)}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes all transactions in {periodLabel(period)}. Other months are
                    not affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={clearAll}
                    data-testid="confirm-clear-button"
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    Delete month
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <a
              href={api.exportUrl(period)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-excel-button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
            >
              <Download className="h-4 w-4 mr-1" /> Export Month
            </a>
            <a
              href={api.exportUrl("")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-all-button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4 mr-1" /> Export All
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">Active period</p>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-gray-900">
              {periodLabel(period)}
            </h2>
          </div>
          {periods.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-end" data-testid="period-chips">
              {periods.map((p) => (
                <button
                  key={p.period}
                  onClick={() => setPeriod(p.period)}
                  data-testid={`period-chip-${p.period}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                    p.period === period
                      ? "bg-black text-white border-black"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {periodLabel(p.period)} · {p.count}
                </button>
              ))}
            </div>
          )}
        </div>

        <UploadZone period={period} onUploaded={load} />

        <KpiCards pnl={pnl} />

        <Tabs defaultValue="transactions">
          <TabsList data-testid="view-tabs">
            <TabsTrigger value="transactions" data-testid="tab-transactions">
              <Receipt className="h-4 w-4 mr-1" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="pnl" data-testid="tab-pnl">
              <LineChart className="h-4 w-4 mr-1" /> Profit & Loss
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <BarChart3 className="h-4 w-4 mr-1" /> Trends
            </TabsTrigger>
          </TabsList>
          <TabsContent value="transactions" className="mt-6 space-y-6">
            <DuplicateBanner groups={dupGroups} onDelete={deleteTxn} />
            <TransactionsTable
              transactions={transactions}
              categories={categories}
              period={period}
              onChange={load}
            />
          </TabsContent>
          <TabsContent value="pnl" className="mt-6">
            <PnlView
              pnl={pnl}
              company={company}
              periodText={periodLabel(period)}
              generatedAt={generatedAt}
            />
          </TabsContent>
          <TabsContent value="trends" className="mt-6">
            <TrendChart refreshKey={`${period}-${pnl.count}`} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
